import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCog, faPencil, faShare, faTrashAlt,
} from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, IconWithMenu, Link, PageHeader,
} from '@domain-group/suit-theme'

import usePublicationBooking from '@hooks/usePublicationBooking'

import { confirmDelete } from '@functions/entity'

import type {
  UsePublicationBookingPlannerPayload,
} from '@components/PublicationBookingPlanner/hooks/usePublicationBookingPlanner'

import PageSelector from './components/PageSelector'
import SelectedEdition from './components/SelectedEdition'
import SelectedPublication from './components/SelectedPublication'

interface HeaderProps {
  bookingPlannerPayload: UsePublicationBookingPlannerPayload,
  isMobile: boolean,
}

const Header = (props: HeaderProps) => {
  const {
    bookingPlannerPayload: {
      callbacks: {
        checkAndPollBookingArtworks,
        checkRemovedBookingItems,
        deletePublicationBooking: deleteFn,
        selectPublicationBookingId,
        showApprovePublicationBookingModal,
        showManagePlannerBookingModal,
        showSelectPublicationEditionModal,
        showSelectPublicationModal,
      },
      editionData: {
        allPagesReleased,
        allSpaceFilled,
        bookingCount,
        isBooked,
        pendingCancellationIds,
      },
      publicationBookingsPayload: {
        filteredPublicationBookings,
        hasFilteredPublicationBookings,
      },
      publicationPayload: {
        publication,
      },
      publicationEditionPayload: {
        publicationEdition,
      },
      selectedPublicationBookingId,
      selectedBookingData: {
        approvedArtworkIds,
        hasBookingNumber,
        isReleased,
        itemCount,
      },
    },
    isMobile,
  } = props

  const publicationBookingPayload = usePublicationBooking({ id: selectedPublicationBookingId })
  const { callbacks: { unapprovePublicationBooking: unapproveFn } } = publicationBookingPayload

  const artworksFilled = approvedArtworkIds ? approvedArtworkIds.length === itemCount : false

  const confirmDeletePayload = {
    callbacks: {
      deleteFn,
    },
    strings: {
      confirmString: 'Are you sure? This will delete the page.',
      deleteString: 'Delete',
      entityName: 'Booking Page',
    },
  }

  const confirmUnapprovePayload = {
    callbacks: {
      deleteFn: unapproveFn,
    },
    strings: {
      confirmString: 'Are you sure? This will unapprove the page on Realhub but the artwork will still be available to the publication.',
      deleteString: 'Unapprove',
      entityName: 'Booking Page',
    },
  }

  return (
    <PageHeader
      boxProps={{ justifyContent: 'flex-start' }}
      heading={(
        <Box style={{ gap: 4 }} width="auto">
          <SelectedPublication onClick={showSelectPublicationModal} publication={publication} />
          <SelectedEdition onClick={showSelectPublicationEditionModal} publicationEdition={publicationEdition} />
          <PageSelector
            callbacks={{ selectPublicationBookingId }}
            publicationBookings={filteredPublicationBookings}
            selectedPublicationBookingId={selectedPublicationBookingId}
          />
        </Box>
      )}
      offset={{ left: 320, top: 0 }}
      width={!isMobile ? 'calc(100% - 320px)' : '100%'}
      actionContent={(
        <Box>
          {hasFilteredPublicationBookings && (
            <IconWithMenu
              boxProps={{ position: 'relative' }}
              icon={(
                <Button
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={faCog} style={{ pointerEvents: 'none' }} />}
                  // @ts-expect-error
                  marginLeft="small"
                  size="large"
                  width="auto"
                />
              )}
            >
              <IconWithMenu.Menu
                closeOnClick
                listStyle={{ right: '0px' }}
                // @ts-expect-error TS2322
                style={{ display: 'inline-flex', flexGrow: 1, width: '100%' }}
              >
                <Link key="manage" onClick={() => showManagePlannerBookingModal()} target="blank">
                  <FontAwesomeIcon icon={faPencil} /> Manage Booking Page
                </Link>

                {/*
                 // @ts-expect-error TS2345 */}
                <Link key="delete" onClick={() => confirmDelete(confirmDeletePayload)} target="blank">
                  <FontAwesomeIcon icon={faTrashAlt} /> Delete Booking Page
                </Link>

                {!isReleased && isBooked && hasBookingNumber && artworksFilled && (
                  <Link
                    key="release"
                    onClick={() => showApprovePublicationBookingModal({ isBulkRelease: true })}
                    target="blank"
                  >
                    <FontAwesomeIcon icon={faShare} /> Release All Bookings
                  </Link>
                )}
              </IconWithMenu.Menu>
            </IconWithMenu>
          )}

          {!isBooked && bookingCount > 0 && allSpaceFilled && !pendingCancellationIds.length && (
            <Button
              // @ts-expect-error TS2322
              as="a"
              buttonStyle="primaryCreate"
              onClick={() => checkRemovedBookingItems()}
              marginLeft="small"
              size="medium"
              width="auto"
            >
              Confirm Booking
            </Button>
          )}

          {!isReleased && isBooked && hasBookingNumber && artworksFilled && (
            <Button
              // @ts-expect-error TS2322
              as="a"
              buttonStyle="primaryCreate"
              disabled={!artworksFilled}
              onClick={() => checkAndPollBookingArtworks()}
              marginLeft="small"
              size="medium"
              width="auto"
            >
              Release Page
            </Button>
          )}

          {isReleased && !allPagesReleased && (
            <Button
              // @ts-expect-error TS2322
              as="a"
              buttonStyle="primaryDestroy"
              // @ts-expect-error TS2345
              onClick={() => confirmDelete(confirmUnapprovePayload)}
              marginLeft="small"
              size="medium"
              width="auto"
            >
              Unapprove
            </Button>
          )}

          <Button
            // @ts-expect-error TS2322
            as="a"
            href="#/"
            buttonStyle="primaryEdit"
            marginLeft="small"
            size="medium"
            width="auto"
          >
            Go Back
          </Button>
        </Box>
      )}
    />
  )
}

export default Header
